import React, { useEffect, useState } from 'react';
import { createSelector } from '@reduxjs/toolkit';
import StationIcon from '@mui/icons-material/Tv';
import { Button, Grid, Paper, Stack, Typography } from '@mui/material';
import { useParams } from 'react-router';

import { UserSettings } from '@paragon/contract-library';
import { designService } from '@paragon/data-connections';
import { getLoggedInUser, hasSupervisorFeatureFlag, isLoggedInUserRoot } from '@paragon/login-tools';
import { enqueueError } from '@paragon/notification-tools';
import { getFallbackUserSettings, UserSettingsDialog } from '@paragon/user-settings';

import { showEditStationDialog, showPermanentLinksDialog } from '../../../actions/dialogActions';
import {
  removeComponentDesignsFromStation,
  updateComponentDesignQuantityInStation,
  uploadComponentDesignsToStation,
} from '../../../actions/stationComponentDesignActions';
import { clearStation, deleteStation, fullyLoadStationIfNeeded } from '../../../actions/supervisorActions';
import { getHolisticViewModel, StationViewModel } from '../../../selectors/holisticViewModelSelector';
import { useAppDispatch, useAppSelector } from '../../hooks';
import { ResponsiveButtonBar } from '../../interface/ResponsiveButtonBar';
import { ErrorPage } from '../ErrorPage';
import { ComponentDesignsList, ScheduleStationComponentDesignsList } from './ComponentDesignsList';
import { LinesList } from './LinesList';

const NOT_FOUND = <ErrorPage body="That station can not be found." />;

const getStationViewModel = createSelector(
  [getHolisticViewModel, (_, stationGuid: string) => stationGuid],
  (holisticViewModel, stationGuid) => {
    if (typeof holisticViewModel === 'string') return holisticViewModel;

    const stationViewModel = holisticViewModel.flatView[stationGuid];
    if (stationViewModel?.type !== 'station') return null;

    return stationViewModel;
  },
);

export function StationPage() {
  const { stationGuid } = useParams();
  const viewModel = useAppSelector((state) => (stationGuid ? getStationViewModel(state, stationGuid) : null));

  if (stationGuid == null || viewModel == null) return NOT_FOUND;
  if (typeof viewModel === 'string') return null;

  return <StationPageContents stationGuid={stationGuid} viewModel={viewModel} />;
}

export function StationPageContents(props: { stationGuid: string; viewModel: StationViewModel }) {
  const { stationGuid, viewModel } = props;

  const dispatch = useAppDispatch();

  const user = useAppSelector(getLoggedInUser)!;
  const isSupervisor = useAppSelector(hasSupervisorFeatureFlag);
  const isRootUser = useAppSelector(isLoggedInUserRoot);

  const station = viewModel.source;

  const [userSettings, setUserSettings] = useState<UserSettings | 'loading' | 'error' | null>(null);
  const [showingUserSettingsDialog, setShowingUserSettingsDialog] = useState(false);

  const actions: [string, () => void][] = [
    ['Rename', () => void dispatch(showEditStationDialog(station.guid))],
    ['Edit Settings', () => void setShowingUserSettingsDialog(true)],
  ];
  if (isRootUser) {
    actions.push(['Delete', () => void dispatch(deleteStation(station))]);
  }

  useEffect(() => {
    setUserSettings(null);
  }, [stationGuid]);

  useEffect(() => {
    if (!isSupervisor) return;
    if (userSettings !== null) return;

    setUserSettings('loading');

    designService.userSettings
      .getUserSettingsForStation(stationGuid)
      .catch((error) => {
        if ((error as any).status !== 404) throw error;
        return getFallbackUserSettings(user.organization, user.permissions);
      })
      .then(setUserSettings)
      .catch((error) => {
        console.error(error);
        setUserSettings('error');
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stationGuid, isSupervisor, userSettings]);

  useEffect(() => {
    dispatch(fullyLoadStationIfNeeded(stationGuid));
  }, [dispatch, stationGuid]);

  return (
    <Stack>
      {showingUserSettingsDialog && typeof userSettings === 'object' && userSettings != null && (
        <UserSettingsDialog
          settings={userSettings}
          onChange={(updatedSettings) => {
            setUserSettings(updatedSettings);

            designService.userSettings.postSetUserSettingsForStation(stationGuid, updatedSettings).catch((error) => {
              console.error(error);

              setShowingUserSettingsDialog(false);
              dispatch(enqueueError('Unable to save user settings at this time.'));
            });
          }}
          requestClose={() => setShowingUserSettingsDialog(false)}
        />
      )}

      <Stack direction="row" sx={{ alignItems: 'center' }}>
        <Typography variant="h3" gutterBottom sx={{ marginRight: 'auto' }}>
          <Stack direction="row" sx={{ alignItems: 'center' }}>
            <StationIcon fontSize="inherit" sx={{ marginRight: 1 }} />
            {station.name}
          </Stack>
        </Typography>

        {isSupervisor && <ResponsiveButtonBar breakpoint="md" actions={actions} />}
      </Stack>

      <Grid container spacing={1}>
        {isSupervisor && (
          <Grid size={12}>
            <Paper sx={{ padding: 2 }}>
              <Typography variant="h5" gutterBottom>
                Station Link
              </Typography>

              <Typography gutterBottom>
                You can access this station from a permanent link without having to log in—get one using the button
                below. Save the link at each computer that should have permanent access to this station.
              </Typography>

              <Button
                variant="outlined"
                sx={{ float: 'right', marginTop: 1, marginRight: 1 }}
                onClick={() => dispatch(showPermanentLinksDialog(station.guid))}
              >
                Manage permanent links
              </Button>

              <div style={{ clear: 'both' }} />
            </Paper>
          </Grid>
        )}

        <Grid size={{ xs: 12, md: 6 }}>
          {station.followsSchedule ? (
            <ScheduleStationComponentDesignsList />
          ) : (
            <ComponentDesignsList
              context="station"
              contextGuid={station.guid}
              componentDesigns={station.componentDesigns}
              onUploadComponentDesigns={(uploadFiles) =>
                dispatch(uploadComponentDesignsToStation(station.guid, uploadFiles))
              }
              onUpdateComponentDesignQuantity={(componentDesignGuid, quantity) =>
                void dispatch(updateComponentDesignQuantityInStation(station.guid, componentDesignGuid, quantity))
              }
              onClearComponentDesigns={() => void dispatch(clearStation(station.guid))}
              onRemoveComponentDesign={(componentDesignGuid: string): void => {
                void dispatch(removeComponentDesignsFromStation(station.guid, componentDesignGuid));
              }}
            />
          )}
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <LinesList station={station} stationViewModel={viewModel} isSupervisor={isSupervisor} />
        </Grid>
      </Grid>
    </Stack>
  );
}
